import * as React from 'react';
import { graphql } from 'gatsby';
import { useLocation } from '@reach/router';
import { useTranslation } from 'react-i18next';
import Layout from '@components/Layout';
import PostGrid from '@components/blog/PostGrid';
import PostHero from '@components/blog/PostHero';
import PostContent from '@components/blog/PostContent';
import PostFooter from '@components/blog/PostFooter';
import PostAuthor from '@components/blog/PostAuthor';
import PostSidebar from '@components/blog/PostSidebar';
import Cta from '@components/sections/Cta/indexCompose';
import Seo from '@components/Seo';
import LatestResources from '@components/LatestResources';

const PostTemplate = ({ data }) => {
  const { t } = useTranslation();

  const postData = data.contentfulBlogPost;
  const cta = data.contentfulComponentCta;
  const introText = postData.introductionText.childMarkdownRemark.html;
  const postContent = postData.postContent;
  const postContentRichText = JSON.parse(postContent.raw);
  const postAuthor = postData.author;
  const sidebarHeadings = postContentRichText.content.filter(
    (item) => item.nodeType === 'heading-2'
  );

  const { allContentfulPlay, allContentfulStreamEpisode } = data;

  const allEdges = [
    ...allContentfulPlay.edges,
    ...allContentfulStreamEpisode.edges,
  ];

  // Sort combined content by date in descending order
  const resources = allEdges
    .map((edge) => edge.node)
    .sort((a, b) => {
      const dateA = new Date(a.datePublished);
      const dateB = new Date(b.datePublished);
      return dateB - dateA;
    })
    .slice(0, 3);

  const postElements = (
    <>
      <PostContent
        introduction={introText}
        content={postContentRichText}
        references={postContent.references}
      />
      <PostFooter
        metaTitle={postData.metaTitle}
        metaDescription={postData.metaDescription}
      />
      <PostAuthor author={postAuthor} content={postAuthor.bio} />
    </>
  );
  const postSidebar = (
    <>
      <PostSidebar headings={sidebarHeadings} />
    </>
  );

  return (
    <Layout progressBar={true}>
      <PostHero
        title={postData.title}
        category={postData.category}
        author={postData.author}
        date={postData.dateModified ?? postData.datePublished}
        otherVersion={postData.otherLanguageVersions}
        image={postData.headerImage}
        metaTitle={postData.metaTitle}
        metaDescription={postData.metaDescription}
      />
      <PostGrid content={postElements} sidebar={postSidebar} />
      <LatestResources resources={resources} />
      <Cta
        mode={cta.mode}
        theme={cta.theme}
        content={{
          heading: cta.title,
          subheading: cta.ctaText,
          windowLocation: cta.windowLocation,
          ctaText: cta.cta.label,
          image: {
            mobile: cta.imageMobile,
            desktop: cta.image,
          },
        }}
      />
    </Layout>
  );
};

export const Head = ({ data, pageContext }) => {
  const location = useLocation();

  return (
    <>
      <html lang={pageContext.language}></html>
      <meta
        property="og:image"
        content={data.contentfulBlogPost.headerImage.url}
      />
      <Seo
        data={data}
        defaultTitle={data.contentfulBlogPost.metaTitle}
        defaultDescription={data.contentfulBlogPost.metaDescription}
      />
      <script type="application/ld+json">
        {`{
      "@context": "https://schema.org",
      "@type": "Article",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "${location.href}"
      },
      "headline": "${data.contentfulBlogPost.title}",
      "description": "${data.contentfulBlogPost.metaDescription}",
      "image": "${data.contentfulBlogPost.headerImage.url}",
      "author": {
        "@type": "Person",
        "name": "${data.contentfulBlogPost.author.fullName}",
        "url": "https://www.dealfront.com/blog/author/${data.contentfulBlogPost.author.slug}"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Dealfront",
        "logo": {
          "@type": "ImageObject",
          "url": "https://images.ctfassets.net/izmg6xmfbr0m/1MuR2kNc8JHlNMWLe7xagM/f1e15a174d9000a3d120e89d3ab20988/dealfront.svg"
        }
      }
    }`}
      </script>
    </>
  );
};

export const query = graphql`
  query ($language: String!, $permalink: String!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    contentfulBlogPost(
      author: { language: { eq: $language } }
      permalink: { eq: $permalink }
    ) {
      language
      title
      metaDescription
      permalink
      metaTitle
      author {
        fullName
        slug
      }
      category {
        name
        slug
      }
      datePublished(formatString: "DD MMMM YYYY", locale: $language)
      dateModified(formatString: "DD MMMM YYYY", locale: $language)
      headerImage {
        url
        title
        gatsbyImageData(width: 650, height: 318)
        description
      }
      otherLanguageVersions {
        permalink
      }
      introductionText {
        childMarkdownRemark {
          html
        }
      }
      postContent {
        raw
        references {
          ... on Node {
            ... on ContentfulBlogIframeEmbed {
              url
              contentful_id
              type
            }
            ... on ContentfulEmbeddedYouTubeVideo {
              videoId
              contentful_id
              type
            }
            ... on ContentfulEmbeddedImage {
              contentful_id
              image {
                gatsbyImageData(width: 1808, height: 1200)
                title
              }
              caption
              type
            }
            ... on ContentfulBlogComponentTipSection {
              contentful_id
              title
              label
              text {
                raw
              }
              type
            }
            ... on ContentfulBlogEmbeddedAdWithImage {
              buttonCopy
              buttonUrl
              image {
                gatsbyImageData(width: 1808, height: 1200)
                title
              }
              contentful_id
              headline
              paragraph
              campaignId
              type
            }
            ... on ContentfulAsset {
              description
              gatsbyImageData
              contentful_id
              title
            }
            ... on ContentfulComponentAccordion {
              contentful_id
              title
              type
              accordionItems {
                title
                content {
                  raw
                }
              }
            }
          }
        }
      }
      author {
        fullName
        slug
        linkedIn
        # The twitter field is commented out because no active author has a Twitter handle, which causes query issues
        # Feel free to uncomment if you have authors with Twitter
        # twitter
        role
        slug
        profilePicture {
          title
          gatsbyImageData(width: 134, height: 134)
          description
        }
        bio {
          raw
        }
      }
    }
    allContentfulPlay(
      filter: { language: { eq: $language }, node_locale: { eq: $language } }
      sort: { datePublished: DESC }
      limit: 3
    ) {
      edges {
        node {
          __typename
          title
          permalink
          headerImage {
            gatsbyImageData(placeholder: NONE, width: 592, height: 311)
          }
          datePublished(formatString: "MMMM DD, YYYY", locale: $language)
        }
      }
    }
    allContentfulStreamEpisode(
      filter: { node_locale: { eq: $language } }
      sort: { airDate: DESC }
      limit: 3
    ) {
      edges {
        node {
          __typename
          permalink: slug
          title
          duration
          datePublished: airDate(
            formatString: "MMMM DD, YYYY"
            locale: $language
          )
          series {
            slug
            title
          }
          headerImage: previewImage {
            gatsbyImageData(placeholder: NONE)
          }
        }
      }
    }
    contentfulComponentCta(
      contentful_id: { eq: "4uqj4xdXWDHWtRUBXwAHeQ" }
      node_locale: { eq: $language }
    ) {
      windowLocation
      internal {
        type
      }
      title
      ctaText: text
      cta {
        label
        url
      }
      icons {
        title
        icon {
          svg {
            originalContent
          }
          title
        }
      }
      mode
      theme
      image {
        gatsbyImageData(placeholder: NONE)
      }
      imageMobile {
        gatsbyImageData(placeholder: NONE)
      }
    }
  }
`;

export default PostTemplate;
